import { useLayoutEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { ChartCrosshairAxisLabel } from './ChartCrosshairAxisLabel';
import type { ChartPlot, ChartPriceLine, ChartPriceLineStyle } from './types';
import { invertLinearScale, type LinearScale } from './utils/scales';

type ChartPriceLinesProps = {
  lines: ChartPriceLine[];
  plot: ChartPlot;
  yDomain: [number, number];
  yScale: LinearScale;
  /** SVG viewport height, used to map pointer positions into SVG space while dragging. */
  height: number;
  formatValue?: (value: number) => string;
};

const DRAG_HANDLE_WIDTH = 12;
const LABEL_OFFSET = 4;

/** Dash patterns scale with stroke width so thicker lines keep the same rhythm. */
const getDashArray = (style: ChartPriceLineStyle, width: number) => {
  switch (style) {
    case 'solid':
      return undefined;
    case 'dashed':
      return `${width * 4} ${width * 4}`;
    case 'dotted':
      return `${width} ${width * 2}`;
    default: {
      const exhaustive: never = style;
      return exhaustive;
    }
  }
};

const getLabelY = (
  position: NonNullable<ChartPriceLine['labelPosition']>,
  lineY: number,
  gap: number,
  labelHeight: number,
) => {
  switch (position) {
    case 'above':
      return lineY - gap - labelHeight;
    case 'below':
      return lineY + gap;
    case 'center':
      return lineY - labelHeight / 2;
    default: {
      const exhaustive: never = position;
      return exhaustive;
    }
  }
};

const LABEL_HEIGHT = 16;
const LABEL_WIDTH_DEFAULT = 48;

type ChartPriceLineLabelProps = {
  text: ReactNode;
  color: string;
  background: string;
  plot: ChartPlot;
  /** Y of the price line; the label sits `gap` px above or below it, or centered on it. */
  lineY: number;
  gap: number;
  position: NonNullable<ChartPriceLine['labelPosition']>;
  align: NonNullable<ChartPriceLine['labelAlign']>;
  offset: number;
  /** SVG viewport height, used to keep the label off the top and bottom edges. */
  height: number;
  action?: ChartPriceLine['action'];
  interactionDisabled?: boolean;
};

/** Caption on a translucent pill so it stays legible over candles. */
const ChartPriceLineLabel = ({
  text,
  color,
  background,
  plot,
  lineY,
  gap,
  position,
  align,
  offset,
  height,
  action,
  interactionDisabled,
}: ChartPriceLineLabelProps) => {
  const [width, setWidth] = useState(LABEL_WIDTH_DEFAULT);
  const textRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const textWidth = textRef.current?.offsetWidth;
    if (!textWidth) return;

    const nextWidth = Math.ceil(textWidth);
    setWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
  }, [text]);

  const labelHeight = action ? 24 : LABEL_HEIGHT;
  const labelWidth = width + (action ? 24 : 0);
  const shift = offset * plot.width;
  const alignedX =
    align === 'left'
      ? plot.left + LABEL_OFFSET + shift
      : align === 'center'
        ? plot.left + (plot.width - labelWidth) / 2 + shift
        : plot.right - LABEL_OFFSET - labelWidth - shift;
  const x = Math.min(Math.max(alignedX, plot.left), plot.right - labelWidth);
  // The plot's vertical padding is shorter than the label, so a line resting against
  // the top or bottom of the plot would push its label off the viewport.
  const y = Math.min(
    Math.max(getLabelY(position, lineY, gap, labelHeight), 0),
    height - labelHeight,
  );

  return (
    <g data-testid="chart-price-line-label" pointerEvents="none">
      {/* Theme-aware hairline (dark on light, light on dark) so overlapping pills stay distinct. */}
      <rect
        x={x}
        y={y}
        width={labelWidth}
        height={labelHeight}
        rx={3}
        fill={background}
        stroke="var(--color-token-alphalight-500)"
        strokeWidth={1}
      />
      <foreignObject x={x} y={y} width={width} height={labelHeight}>
        {/* `shrink-0` + nowrap keep the measured width honest while the box is still the default size. */}
        <div
          ref={textRef}
          className={fontStyles({
            variant: 'CAPTION',
            class: [
              'flex',
              'h-full',
              'w-fit',
              'shrink-0',
              'items-center',
              'gap-1',
              'px-1',
              'whitespace-nowrap',
            ],
          })}
          style={{ color }}
        >
          {text}
        </div>
      </foreignObject>
      {action && (
        <foreignObject x={x + width} y={y} width={24} height={24} pointerEvents="auto">
          <button
            type="button"
            aria-label={action.label}
            title={action.label}
            aria-busy={action.pending || undefined}
            disabled={interactionDisabled || action.disabled || action.pending}
            className={buttons({
              size: 'extraSmall',
              color: 'alphaGray',
              shape: 'roundedSubtle',
              class: 'w-6 p-0',
            })}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              action.onClick();
            }}
          >
            <span aria-hidden="true">{action.pending ? '…' : '×'}</span>
          </button>
        </foreignObject>
      )}
    </g>
  );
};

type PriceLineProps = Omit<ChartPriceLinesProps, 'lines'> & {
  line: ChartPriceLine;
  labelLayer: SVGGElement | null;
};

const PriceLine = ({
  line,
  plot,
  yDomain,
  yScale,
  height,
  formatValue,
  labelLayer,
}: PriceLineProps) => {
  const {
    value,
    label,
    color,
    lineStyle = 'dashed',
    lineWidth = 1,
    labelPosition = 'above',
    labelAlign = 'right',
    labelOffset = 0,
    labelBackground = 'var(--color-token-alphalight-500)',
    labelColor = color,
    onValueChange,
    onValueCommit,
    interactionDisabled,
    action,
  } = line;
  const [preview, setPreview] = useState<number>();
  const commitVersion = useRef(0);
  const drag = useRef<{ pointerId: number; startValue: number; nextValue?: number } | undefined>(
    undefined,
  );
  const disabled = interactionDisabled || action?.pending;
  const commitOnDrop = Boolean(onValueCommit);
  // The dropped position is held as a preview until the committed value arrives.
  useLayoutEffect(() => {
    if (commitOnDrop) drag.current = undefined;
    setPreview(undefined);
  }, [value, commitOnDrop]);
  // Becoming disabled mid-drag cancels the drag; becoming enabled again means the commit
  // settled (or failed) without the value changing, so the preview is stale either way.
  useLayoutEffect(() => {
    if (!disabled) setPreview(undefined);
    else if (drag.current) {
      drag.current = undefined;
      setPreview(undefined);
    }
  }, [disabled]);
  const displayValue = preview ?? value;
  const y = yScale(displayValue);

  const toValue = (event: PointerEvent<SVGLineElement>) => {
    const bounds = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
    if (!bounds || bounds.height === 0) return undefined;
    const svgY = ((event.clientY - bounds.top) / bounds.height) * height;
    const clampedY = Math.min(Math.max(svgY, plot.top), plot.bottom);
    return invertLinearScale(clampedY, yDomain, [plot.bottom, plot.top]);
  };

  // Only an in-progress drag is cancelled; the `lostpointercapture` that follows a drop
  // must not discard the preview being held for the commit.
  const resetDrag = () => {
    if (!drag.current) return;
    drag.current = undefined;
    setPreview(undefined);
  };

  if (!Number.isFinite(displayValue) || y < plot.top || y > plot.bottom) return null;

  return (
    <g data-testid="chart-price-line">
      <line
        x1={plot.left}
        x2={plot.right}
        y1={y}
        y2={y}
        stroke={color}
        strokeWidth={lineWidth}
        strokeDasharray={getDashArray(lineStyle, lineWidth)}
        pointerEvents="none"
      />
      {(onValueChange || onValueCommit) && !disabled && (
        <line
          data-testid="chart-price-line-handle"
          x1={plot.left}
          x2={plot.right}
          y1={y}
          y2={y}
          stroke="transparent"
          strokeWidth={Math.max(DRAG_HANDLE_WIDTH, lineWidth)}
          className="cursor-ns-resize touch-none"
          onPointerDown={(event) => {
            if (event.button !== 0 || drag.current) return;
            commitVersion.current += 1;
            event.stopPropagation();
            event.preventDefault();
            event.currentTarget.setPointerCapture?.(event.pointerId);
            drag.current = { pointerId: event.pointerId, startValue: value };
          }}
          onPointerMove={(event) => {
            if (!drag.current || drag.current.pointerId !== event.pointerId) return;
            event.stopPropagation();
            const nextValue = toValue(event);
            if (nextValue == null || !Number.isFinite(nextValue)) return;
            drag.current.nextValue = nextValue;
            if (onValueCommit) setPreview(nextValue);
            else onValueChange?.(nextValue);
          }}
          onPointerUp={async (event) => {
            if (!drag.current || drag.current.pointerId !== event.pointerId) return;
            event.stopPropagation();
            const { startValue, nextValue } = drag.current;
            drag.current = undefined;
            event.currentTarget.releasePointerCapture?.(event.pointerId);
            if (nextValue != null && nextValue !== startValue && onValueCommit) {
              const version = commitVersion.current;
              try {
                const started = await onValueCommit(nextValue);
                if (!started && version === commitVersion.current) setPreview(undefined);
              } catch {
                if (version === commitVersion.current) setPreview(undefined);
              }
            } else {
              setPreview(undefined);
            }
          }}
          onPointerCancel={resetDrag}
          onLostPointerCapture={resetDrag}
        />
      )}
      {(label || action) &&
        labelLayer &&
        createPortal(
          <ChartPriceLineLabel
            text={label ?? ''}
            color={labelColor}
            background={labelBackground}
            plot={plot}
            lineY={y}
            gap={LABEL_OFFSET + lineWidth / 2}
            position={labelPosition}
            align={labelAlign}
            offset={labelOffset}
            height={height}
            action={action}
            interactionDisabled={interactionDisabled}
          />,
          labelLayer,
        )}
      {formatValue && (
        <ChartCrosshairAxisLabel
          axis="y"
          plot={plot}
          position={y}
          label={formatValue(displayValue)}
          fill={color}
          textFill="var(--primitives-color-gray-100)"
        />
      )}
    </g>
  );
};

export const ChartPriceLines = ({ lines, ...props }: ChartPriceLinesProps) => {
  // SVG paints in document order, so labels are portaled into a layer after every line
  // to keep them from being drawn over by lines declared later.
  const [labelLayer, setLabelLayer] = useState<SVGGElement | null>(null);
  return (
    <>
      {lines.map((line) => (
        <PriceLine key={line.key} line={line} labelLayer={labelLayer} {...props} />
      ))}
      <g ref={setLabelLayer} data-testid="chart-price-line-labels" />
    </>
  );
};
